import {
  Button,
  Card,
  Checkbox,
  CheckboxGroup,
  Flex,
  Icon,
  Input,
  ModalManagerContext,
  Text,
  Tooltip,
  useElement,
} from '@pega/cosmos-react-core';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import { ColumnProps } from '@pega/cosmos-react-core/lib/components/Table/Table';
import React, { useContext, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { updateHistoryEnabledPayload, updateRequiresAuthenticationPayload } from '../../../../interfaces/chat';
import { appState, customModalProps, SDKComponentProps } from '../../../../interfaces/common';
import { setChatInfo, updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { usePlatformVersionSelector } from '../../../../redux/selectors';
import { StyledTable, tokenMaskerStyle } from '../../../Utilities/custom.styled';
import { CustomModal } from '../../../Utilities/CustomModal';
import { addNewCookie, addNewDomain, deleteCookie, deleteDomain, updateUIToggle } from '../ChatApis';

interface TemplateSectionProps {
  name: string;
  identifier: string;
  data: string[];
  header: string;
  description: string;
  addCallback: (name: string) => void;
  deleteCallback: (name: string) => void;
  validationFn: (url: string) => boolean | string;
}

const TemplateSection: React.FC<TemplateSectionProps> = (props) => {
  const { name, identifier, data, header, description, addCallback, deleteCallback, validationFn } = props;
  const { register, handleSubmit, errors, reset, formState } = useForm({
    mode: 'all',
  });
  const translate = useLocalTranslation();
  const tableData = data.map((item, index) => {
    return {
      id: index,
      [identifier]: (
        <>
          <Flex
            container={{
              direction: 'row',
              justify: 'between',
              alignItems: 'center',
            }}
          >
            <Text>{item}</Text>
            <Button
              variant="simple"
              icon
              onClick={() => {
                deleteCallback(item);
              }}
            >
              <Icon name="trash" />
            </Button>
          </Flex>
        </>
      ),
    };
  });
  const tableColumns: ColumnProps<(typeof tableData)[number]>[] = [{ renderer: identifier, label: name }];
  const onAdd = async (data: { [identifier: string]: string }) => {
    addCallback(data[identifier]);
    reset({ [identifier]: '' });
  };

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 1.5,
      }}
    >
      <Text as="h2" variant="h2">
        {header}
      </Text>
      <Text>{description}</Text>
      <Flex
        container={{
          direction: 'row',
          alignItems: errors[identifier]?.message ? 'center' : 'end',
          itemGap: 1,
        }}
      >
        <Flex item={{ grow: 1 }}>
          <Input
            item={{ grow: 1 }}
            label={`${translate('new')} ${identifier}`}
            type="text"
            name={identifier}
            required={true}
            autoComplete="off"
            info={errors[identifier]?.message}
            status={errors[identifier]?.message ? 'error' : undefined}
            ref={register({
              required: `${capitalizeFirstLetter(identifier)} ${translate('cannot_be_empty')}`,
              validate: validationFn,
            })}
          />
        </Flex>
        <Button type="submit" onClick={handleSubmit(onAdd)} disabled={!formState.isValid}>
          {translate('add')}
        </Button>
      </Flex>
      <Card style={{ padding: 0 }}>
        <StyledTable hoverHighlight={true} padding={'default'} columns={tableColumns} data={tableData} />
      </Card>
    </Flex>
  );
};

const SecurityTab: React.FC<SDKComponentProps> = ({ platform }: SDKComponentProps) => {
  const {
    cors = [],
    cookies = [],
    connectionId,
    jwtToken,
    requiresAuth,
    historyEnabled,
    syncConversationAcrossChannels,
  } = useSelector((state: appState) => state.chatInfo);
  const sessionId = useSelector((state: appState) => state.sessionId);
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const navigate = useNavigate();
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<customModalProps>>>();
  const { majorVersion, minorVersion } = usePlatformVersionSelector();
  const beyondEightDotSeven =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 7));
  const [toolTip, setToolTip] = useElement();

  const addDomainCallback = async (name: string) => {
    const payload = { domain: name };
    dispatch(updateLoaderDetails(true));
    const json = await addNewDomain(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(`${name} ${translate('added_to_domains')}`, 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(`${translate('add_to_domains_failed')}`, 'urgent'));
    }
  };

  const deleteDomainCallback = async (name: string) => {
    const payload = { domain: name };
    dispatch(updateLoaderDetails(true));
    const json = await deleteDomain(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(`${name} ${translate('domain_deleted')}`, 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('domain_delete_failed'), 'urgent'));
    }
  };

  const addCookieCallback = async (name: string) => {
    const payload = { cookie: name };
    dispatch(updateLoaderDetails(true));
    const json = await addNewCookie(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(`${name} ${translate('added_to_cookies')}`, 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('add_to_cookies_failed'), 'urgent'));
    }
  };

  const deleteCookieCallback = async (name: string) => {
    const payload = { cookie: name };
    dispatch(updateLoaderDetails(true));
    const json = await deleteCookie(sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(`${name} ${translate('cookie_deleted')}`, 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate('cookie_delete_failed'), 'urgent'));
    }
  };

  const updateRequiresAuthentication = async (e: any) => {
    if (e.target.checked) {
      modalMethods.current = create<customModalProps>(CustomModal, {
        heading: translate('chat_session_control_confirm'),
        children: <Text>{translate('chat_session_control_confirmation_agreement')}</Text>,
        actions: (
          <>
            <Button
              onClick={() => {
                modalMethods.current?.dismiss();
              }}
            >
              {translate('cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                await updateToggleConfig('requires_authentication', { requiresAuth: true });
                modalMethods.current?.dismiss();
              }}
            >
              {translate('ok')}
            </Button>
          </>
        ),
      });
    } else {
      await updateToggleConfig('requires_authentication', { requiresAuth: e.target.checked });
    }
  };

  const updateHistoryEnabled = async (e: any) => {
    await updateToggleConfig('previous_interactions', { historyEnabled: e.target.checked });
  };

  const updateToggleConfig = async (
    togglePath: string,
    payload: updateRequiresAuthenticationPayload | updateHistoryEnabledPayload,
  ) => {
    dispatch(updateLoaderDetails(true));
    let json = await updateUIToggle(togglePath, sessionId, connectionId, payload);
    dispatch(updateLoaderDetails(false));
    if (json?.status === 200) {
      dispatch(setChatInfo(json?.data));
      dispatch(updateBannerInfo(translate(`${togglePath}_update_success`), 'success'));
    } else if (json?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(translate('invalid_session'), 'urgent'));
    } else {
      dispatch(updateBannerInfo(translate(`${togglePath}_update_failed`), 'urgent'));
    }
  };

  const domainValidation = (url: string) => {
    if (url === '*') {
      return true;
    }

    const validationMsg: string = translate('domain_validation_message');

    const pattern = new RegExp(
      `^https?://[a-z0-9]+(?:[-.][a-z0-9]+)*(?::[0-9]{1,5})?(?:/[^/\r\n]+)*.[a-z]{2,5}(?:[?#]S*)?$`,
    );
    return !!pattern.test(url) ? true : validationMsg;
  };

  const cookieValidation = (cookie: string) => {
    const validationMsg: string = translate('validate_cookie_error');

    const cookiePattern = /^[A-Za-z](\w+$)/;

    if (cookies.indexOf(cookie) !== -1) {
      return validationMsg;
    }

    return !!cookiePattern.test(cookie) ? true : validationMsg;
  };

  const domainProps = {
    name: translate('domains'),
    identifier: 'domain',
    data: cors,
    header: translate('allowed_domains'),
    description: translate('domains_section_description'),
    addCallback: addDomainCallback,
    deleteCallback: deleteDomainCallback,
    validationFn: domainValidation,
  };

  const cookiesProps = {
    name: translate('cookies'),
    identifier: 'cookie',
    data: cookies,
    header: translate('cookies'),
    description: translate('cookies_section_description'),
    addCallback: addCookieCallback,
    deleteCallback: deleteCookieCallback,
    validationFn: cookieValidation,
  };

  const [isMaskEnabled, setIsMaskEnabled] = useState(true);
  const getMaskedToken = (token: string = '') => {
    return !isMaskEnabled ? token : new Array(token.length).fill('*').join('');
  };
  return (
    <Flex
      container={{
        direction: 'column',
        itemGap: 3,
      }}
    >
      {!platform && (
        <>
          <TemplateSection {...domainProps} />
          <TemplateSection {...cookiesProps} />
          {beyondEightDotSeven && (
            <Flex
              container={{
                direction: 'column',
                itemGap: 1,
              }}
            >
              <Text variant="h2" as="h2">
                {translate('chat_session_control')}
              </Text>
              <CheckboxGroup name="requiresAuthToggle" ref={setToolTip}>
                <>
                  <Checkbox
                    label={translate('chat_session_control_toggle')}
                    checked={Boolean(requiresAuth)}
                    onChange={updateRequiresAuthentication}
                    disabled={syncConversationAcrossChannels ?? false}
                  />
                  {syncConversationAcrossChannels && (
                    <Tooltip target={toolTip}>{translate('session_control_disable_note')}</Tooltip>
                  )}
                </>
              </CheckboxGroup>
              {requiresAuth && (
                <CheckboxGroup name="enableHistoryToggle">
                  <Checkbox
                    label={translate('previous_interactions')}
                    checked={Boolean(historyEnabled)}
                    onChange={updateHistoryEnabled}
                  />
                </CheckboxGroup>
              )}
            </Flex>
          )}
        </>
      )}
      {platform && (
        <Flex
          container={{
            direction: 'column',
            itemGap: 1,
          }}
        >
          <Text variant="h2" as="h2">
            {translate('sdk_async_history')}
          </Text>
          <CheckboxGroup name="enableHistoryToggle">
            <Checkbox
              label={translate('previous_interactions')}
              checked={Boolean(historyEnabled)}
              onChange={updateHistoryEnabled}
            />
          </CheckboxGroup>
        </Flex>
      )}
      {jwtToken && (
        <Flex
          container={{
            direction: 'column',
            itemGap: 1.5,
          }}
        >
          <Text variant="h2" as="h2">
            {translate('jwt_secret_label')}
          </Text>
          <Text>{translate(platform ? 'jwt_secret_description_sdk' : 'jwt_secret_description')}</Text>
          <Text variant="secondary">{getMaskedToken(jwtToken)}</Text>
          <Flex>
            <Button style={tokenMaskerStyle} onClick={() => setIsMaskEnabled(!isMaskEnabled)}>
              {`${translate(isMaskEnabled ? 'show' : 'hide')} ${translate('jwt_secret_label')}`}
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default SecurityTab;
